import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Edit2, Eye, FileText, Plus, Trash2, Upload, User, X } from 'lucide-react';
import { toast } from 'sonner';
import { corporateCarrierService } from '../services/corporateCarrierService';
import { driverService } from '../services/driverService';
import { vehicleService } from '../services/vehicleService';
import { documentService } from '../services/documentService';
import { getStoredDriverMobileUser } from '../utils/driverMobileSession';
import { DriverBottomNav } from '../components/DriverBottomNav';

const DOC_TYPES = [
  { key: 'license', label: 'Ehliyet' },
  { key: 'src', label: 'SRC Belgesi' },
  { key: 'psychotechnic', label: 'Psikoteknik Belgesi' },
];

export function DriverMobileCorporateDrivers() {
  const navigate = useNavigate();
  const user = getStoredDriverMobileUser<any>();
  const carrierId = String(user?.externalTransportAccountUserId || user?.external_transport_account_user_id || user?.id || '');
  const [drivers, setDrivers] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingDriver, setEditingDriver] = useState<any | null>(null);
  const emptyForm = { full_name: '', phone: '', email: '', tc_number: '', city: '', district: '', vehicle_id: '', cekici_plaka: '', dorse_plaka: '', vehicle_type: '', license_number: '', license_class: 'E', experience_years: 0 };
  const [form, setForm] = useState(emptyForm);
  const apiRoot = String(import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

  const getDocumentUrl = (document: any) => {
    const path = String(document?.file_path || document?.filePath || '');
    if (!path) return '#';
    if (/^https?:\/\//i.test(path)) return path;
    return `${apiRoot}${path.startsWith('/') ? path : `/${path}`}`;
  };

  const getLatestDocument = (driver: any, documentType: string) => {
    const groups = driver?.documentsByType || driver?.documents_by_type || {};
    const docs = groups[documentType] || [];
    return Array.isArray(docs) && docs.length > 0 ? docs[0] : null;
  };

  const loadDrivers = async () => {
    try {
      setLoading(true);
      const [driversResponse, vehiclesResponse]: any[] = await Promise.all([
        corporateCarrierService.getDrivers(carrierId),
        vehicleService.getVehicles({ ownerType: 'external', externalTransportAccountUserId: carrierId }).catch(() => ({ data: [] })),
      ]);
      const response = driversResponse;
      setDrivers(Array.isArray(response?.data) ? response.data : []);
      setVehicles(Array.isArray(vehiclesResponse?.data) ? vehiclesResponse.data : []);
    } catch (error) {
      console.error('Corporate mobile drivers load failed:', error);
      toast.error('Sürücüler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/driver-mobile-auth', { replace: true });
      return;
    }
    void loadDrivers();
  }, [carrierId]);

  const saveDriver = async () => {
    if (!form.full_name.trim() || !form.phone.trim()) {
      toast.warning('Ad soyad ve telefon zorunludur');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...form,
        full_name: form.full_name.toLocaleUpperCase('tr-TR'),
        city: form.city.toLocaleUpperCase('tr-TR'),
        district: form.district.toLocaleUpperCase('tr-TR'),
        cekici_plaka: form.cekici_plaka.toLocaleUpperCase('tr-TR').replace(/\s+/g, ''),
        dorse_plaka: form.dorse_plaka.toLocaleUpperCase('tr-TR').replace(/\s+/g, ''),
        vehicle_type: form.vehicle_type.toLocaleUpperCase('tr-TR'),
        external_transport_account_user_id: carrierId,
      };
      if (editingDriver?.id) await driverService.updateApplication(editingDriver.id, payload);
      else await driverService.createApplication(payload);
      setForm(emptyForm);
      setEditingDriver(null);
      await loadDrivers();
      toast.success(editingDriver ? 'Sürücü güncellendi' : 'Sürücü kaydedildi');
    } catch (error: any) {
      toast.error(error?.message || 'Sürücü kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const selectVehicle = (vehicleId: string) => {
    const vehicle = vehicles.find((item) => String(item.id) === String(vehicleId));
    setForm((current) => ({
      ...current,
      vehicle_id: vehicleId,
      cekici_plaka: vehicle?.cekici_plaka || vehicle?.plate || '',
      dorse_plaka: vehicle?.dorse_plaka || vehicle?.trailer_plate || '',
      vehicle_type: vehicle?.vehicle_type || '',
    }));
  };

  const startEdit = (driver: any) => {
    const matchedVehicle = vehicles.find((vehicle) => {
      const tractor = String(vehicle.cekici_plaka || vehicle.plate || '').replace(/\s+/g, '').toLocaleUpperCase('tr-TR');
      const trailer = String(vehicle.dorse_plaka || vehicle.trailer_plate || '').replace(/\s+/g, '').toLocaleUpperCase('tr-TR');
      return tractor === String(driver.cekiciPlaka || '').replace(/\s+/g, '').toLocaleUpperCase('tr-TR') && (!driver.dorsePlaka || trailer === String(driver.dorsePlaka || '').replace(/\s+/g, '').toLocaleUpperCase('tr-TR'));
    });
    setEditingDriver(driver);
    setForm({
      ...emptyForm,
      full_name: driver.fullName || '',
      phone: driver.phone || '',
      email: driver.email || '',
      tc_number: driver.tcNumber || '',
      city: driver.city || '',
      district: driver.district || '',
      vehicle_id: matchedVehicle ? String(matchedVehicle.id) : '',
      cekici_plaka: driver.cekiciPlaka || '',
      dorse_plaka: driver.dorsePlaka || '',
      vehicle_type: driver.vehicleType || '',
      license_number: driver.licenseNumber || '',
      license_class: driver.licenseClass || 'E',
      experience_years: Number(driver.experienceYears || 0),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingDriver(null);
    setForm(emptyForm);
  };

  const uploadDocument = async (driver: any, documentType: string, file?: File | null) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entityType', 'driver_application');
      formData.append('entityId', String(driver.id));
      formData.append('documentType', documentType);
      formData.append('name', DOC_TYPES.find((item) => item.key === documentType)?.label || documentType);
      await documentService.uploadDocument(formData);
      await loadDrivers();
      toast.success('Belge yüklendi');
    } catch (error: any) {
      toast.error(error?.message || 'Belge yüklenemedi');
    }
  };

  const deleteDocument = async (document: any) => {
    if (!document?.id) return;
    if (!window.confirm(`${document.file_name || 'Belge'} silinsin mi?`)) return;
    try {
      await documentService.deleteDocument(document.id);
      await loadDrivers();
      toast.success('Belge silindi');
    } catch (error: any) {
      toast.error(error?.message || 'Belge silinemedi');
    }
  };

  const deleteDriver = async (driver: any) => {
    if (!window.confirm(`${driver.fullName || 'Sürücü'} silinsin mi?`)) return;
    try {
      setDeletingId(String(driver.id));
      await driverService.deleteApplication(driver.id);
      if (editingDriver?.id === driver.id) cancelEdit();
      await loadDrivers();
      toast.success('Sürücü silindi');
    } catch (error: any) {
      toast.error(error?.message || 'Sürücü silinemedi');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50">
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white p-4 shadow-sm">
        <button onClick={() => navigate('/driver-mobile-profile')} className="mb-3 inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 font-semibold text-slate-900"><ArrowLeft className="h-5 w-5" /> Geri</button>
        <h1 className="text-xl font-bold text-slate-900">Sürücüler</h1>
        <p className="text-sm text-slate-500">Sürücü ekleyin ve sürücü belgelerini yükleyin.</p>
      </div>
      <div className="space-y-4 p-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-2 font-bold text-slate-900">
            <span className="inline-flex items-center gap-2">{editingDriver ? <Edit2 className="h-5 w-5" /> : <Plus className="h-5 w-5" />} {editingDriver ? 'Sürücü Düzenle' : 'Yeni Sürücü'}</span>
            {editingDriver ? <button type="button" onClick={cancelEdit} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-700"><X className="h-3.5 w-3.5" /> Vazgeç</button> : null}
          </div>
          <div className="grid gap-3">
            <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Ad Soyad" className="rounded-xl border border-slate-300 px-3 py-3" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 11) })} placeholder="Telefon" className="rounded-xl border border-slate-300 px-3 py-3" />
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-posta" className="rounded-xl border border-slate-300 px-3 py-3" />
            <input value={form.tc_number} onChange={(e) => setForm({ ...form, tc_number: e.target.value.replace(/\D/g, '').slice(0, 11) })} placeholder="TC Kimlik No" className="rounded-xl border border-slate-300 px-3 py-3" />
            <select value={form.vehicle_id} onChange={(e) => selectVehicle(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-3 py-3">
              <option value="">Kayıtlı çekici seçin</option>
              {vehicles.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>{vehicle.cekici_plaka || vehicle.plate || '-'} / {vehicle.dorse_plaka || vehicle.trailer_plate || '-'} · {vehicle.vehicle_type || '-'}</option>
              ))}
            </select>
            {form.vehicle_id ? <div className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">Dorse: {form.dorse_plaka || '-'} · Araç tipi: {form.vehicle_type || '-'}</div> : null}
            <button disabled={saving} onClick={saveDriver} className="rounded-xl bg-blue-600 px-4 py-3 font-bold text-white disabled:opacity-60">{editingDriver ? 'Güncelle' : 'Kaydet'}</button>
          </div>
        </div>

        {loading ? <div className="rounded-xl bg-white p-6 text-center text-slate-500">Yükleniyor...</div> : drivers.map((driver) => (
          <div key={driver.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3"><User className="mt-1 h-5 w-5 text-blue-600" /><div><div className="font-bold text-slate-900">{driver.fullName || '-'}</div><div className="text-sm text-slate-500">{driver.phone || '-'} · {driver.cekiciPlaka || '-'} / {driver.dorsePlaka || '-'}</div></div></div>
              <div className="flex gap-2">
                <button type="button" onClick={() => startEdit(driver)} className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-bold text-blue-700"><Edit2 className="h-4 w-4" /></button>
                <button type="button" disabled={deletingId === String(driver.id)} onClick={() => void deleteDriver(driver)} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 disabled:opacity-60"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="mt-4 grid gap-2">
              {DOC_TYPES.map((doc) => {
                const existingDocument = getLatestDocument(driver, doc.key);
                return (
                  <div key={doc.key} className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700">
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex min-w-0 items-center gap-2 font-semibold"><FileText className="h-4 w-4 shrink-0" /> {doc.label}</span>
                      <span className={`rounded-lg px-2 py-1 text-xs font-bold ${existingDocument ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{existingDocument ? 'Yüklü' : 'Eksik'}</span>
                    </div>
                    {existingDocument ? (
                      <div className="mt-2 rounded-lg bg-slate-50 p-2">
                        <div className="truncate text-xs font-medium text-slate-700">{existingDocument.file_name || existingDocument.fileName || doc.label}</div>
                        <div className="mt-1 text-[11px] text-slate-500">{existingDocument.uploaded_at ? new Date(existingDocument.uploaded_at).toLocaleString('tr-TR') : ''}</div>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                          <a href={getDocumentUrl(existingDocument)} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1 rounded-lg bg-blue-50 px-2 py-2 text-xs font-bold text-blue-700"><Eye className="h-3.5 w-3.5" /> Önizle</a>
                          <label className="inline-flex items-center justify-center gap-1 rounded-lg bg-slate-100 px-2 py-2 text-xs font-bold text-slate-700">
                            <Upload className="h-3.5 w-3.5" /> Değiştir
                            <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => void uploadDocument(driver, doc.key, e.target.files?.[0])} />
                          </label>
                          <button type="button" onClick={() => void deleteDocument(existingDocument)} className="inline-flex items-center justify-center gap-1 rounded-lg bg-rose-50 px-2 py-2 text-xs font-bold text-rose-700"><Trash2 className="h-3.5 w-3.5" /> Sil</button>
                        </div>
                      </div>
                    ) : (
                      <label className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-lg bg-slate-100 px-2 py-2 text-xs font-bold text-slate-700">
                        <Upload className="h-3.5 w-3.5" /> Yükle
                        <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => void uploadDocument(driver, doc.key, e.target.files?.[0])} />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <DriverBottomNav />
    </div>
  );
}
